// SSR-safe: Defer environment check to runtime
function isDevelopmentMode(): boolean {
  // In browser with Vite
  if (typeof window !== 'undefined' && typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.DEV === true;
  }
  // In Node.js/SSR
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  // Fallback
  return false;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
    module: string;
    level?: LogLevel;
    enabled?: boolean;
}

interface LogData {
    [key: string]: unknown;
}

class Logger {
    private module: string;
    private enabled: boolean;
    private level: LogLevel;

    constructor({ module, level = 'info', enabled }: LoggerOptions) {
        this.module = module;
        this.level = level;
        // Defer isDevelopment check to runtime instead of module load time
        this.enabled = enabled !== undefined ? enabled : isDevelopmentMode();
    }

    private shouldLog(level: LogLevel): boolean {
        if (!this.enabled) return false;
        
        const levels: Record<LogLevel, number> = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3
        };

        return levels[level] >= levels[this.level];
    }

    private formatMessage(message: string, data?: LogData): string {
        const timestamp = new Date().toISOString();
        const dataString = data ? ` ${JSON.stringify(data)}` : '';
        return `[${timestamp}] [${this.module}] ${message}${dataString}`;
    }

    private isNetworkError(message: string, error?: Error | unknown, data?: LogData): boolean {
        // Check if we're offline (only in browser environment)
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return true;
        }

        // Check error object for network-related messages
        if (error) {
            const errorStr = typeof error === 'string' ? error :
                           error instanceof Error ? error.message :
                           JSON.stringify(error);

            if (errorStr.includes('ERR_INTERNET_DISCONNECTED') ||
                errorStr.includes('Failed to fetch') ||
                errorStr.includes('NetworkError') ||
                errorStr.includes('ERR_NETWORK') ||
                errorStr.includes('Network disconnected') ||
                errorStr.includes('NETWORK_ERROR')) {
                return true;
            }
        }

        // Check data object for network indicators
        if (data) {
            if (data.offline === true ||
                data.code === 'NETWORK_ERROR' ||
                data.error === 'Network disconnected' ||
                (typeof data.error === 'string' && data.error.includes('Network disconnected'))) {
                return true;
            }
        }

        // Check the message itself
        if (message.includes('Network disconnected') ||
            message.includes('ERR_INTERNET_DISCONNECTED') ||
            message.includes('offline')) {
            return true;
        }

        return false;
    }

    debug(message: string, data?: LogData): void {
        if (this.shouldLog('debug')) {
            console.debug(this.formatMessage(message, data));
        }
    }

    info(message: string, data?: LogData): void {
        if (this.shouldLog('info')) {
            console.info(this.formatMessage(message, data));
        }
    }

    warn(message: string, data?: LogData | unknown): void {
        if (this.shouldLog('warn')) {
            let logData: LogData | undefined;
            
            // Check if data is a valid LogData object or needs conversion
            if (data !== undefined) {
                if (data === null || typeof data !== 'object' || Array.isArray(data)) {
                    // Handle non-object data by converting to a LogData object
                    logData = { value: data, type: typeof data };
                } else {
                    try {
                        // Try to use as LogData directly, but safely typecast
                        logData = data as LogData;
                    } catch (_e) {
                        // Fallback if typecasting fails
                        logData = { value: String(data), type: typeof data };
                    }
                }
            }
            
            console.warn(this.formatMessage(message, logData));
        }
    }

    error(message: string, error?: Error | unknown, data?: LogData): void {
        // Check if this is actually a network error that should be logged as info
        const isNetworkError = this.isNetworkError(message, error, data);

        if (isNetworkError) {
            // Redirect network errors to info logging
            this.info(message.replace('Error', 'Network disconnected'), data);
            return;
        }

        if (this.shouldLog('error')) {
            let errorData: LogData = {};

            // Add error information to the log data
            if (error !== undefined) {
                if (error instanceof Error) {
                    // Handle standard Error objects
                    errorData = {
                        name: error.name,
                        message: error.message,
                        stack: error.stack,
                    };
                } else if (error !== null && typeof error === 'object') {
                    // Handle object-like errors that aren't Error instances
                    try {
                        // Try to JSON.stringify the error first
                        const jsonStr = JSON.stringify(error, (key, value) => {
                            // Handle circular references
                            if (typeof value === 'object' && value !== null) {
                                if (value instanceof Error) {
                                    // Spread first, then override with specific properties
                                    const errorObj = value as unknown as Record<string, unknown>;
                                    return {
                                        ...errorObj,
                                        name: value.name,
                                        message: value.message,
                                        stack: value.stack
                                    };
                                }
                            }
                            return value;
                        });
                        
                        // If we can stringify it, parse it back to get a clean object
                        if (jsonStr && jsonStr !== '{}') {
                            errorData = JSON.parse(jsonStr);
                        } else {
                            // Fallback to manual extraction
                            const errorObj = error as Record<string, unknown>;
                            
                            // Extract all enumerable properties
                            errorData = {};
                            for (const key in errorObj) {
                                try {
                                    const value = errorObj[key];
                                    if (typeof value !== 'function') {
                                        errorData[key] = value;
                                    }
                                } catch (_err) {
                                    // Skip properties that throw on access
                                }
                            }
                            
                            // Also get non-enumerable properties
                            const propNames = Object.getOwnPropertyNames(errorObj);
                            for (const key of propNames) {
                                if (!(key in errorData)) {
                                    try {
                                        const value = errorObj[key];
                                        if (typeof value !== 'function' && key !== 'constructor') {
                                            errorData[key] = value;
                                        }
                                    } catch (_err) {
                                        // Skip properties that throw on access
                                    }
                                }
                            }
                            
                            // Add type info
                            errorData._type = errorObj.constructor?.name || 'Object';
                            errorData._toString = String(error);
                        }
                    } catch (e) {
                        // Final fallback
                        errorData = {
                            error: String(error),
                            _extractionError: String(e)
                        };
                    }
                } else {
                    // Handle primitive error types
                    errorData = { 
                        error_value: error,
                        error_type: typeof error
                    };
                }
            }
            
            // Merge with any additional data provided
            if (data) {
                errorData = { ...errorData, ...data };
            }
            
            console.error(this.formatMessage(message, errorData));
        }
    }

    // Special method for component renders - only logs in debug mode and throttled
    render(data?: LogData) {
        if (this.shouldLog('debug')) {
            // Using a WeakMap to store last render time per component
            if (!Logger.renderTimes.has(this)) {
                Logger.renderTimes.set(this, 0);
            }

            const now = Date.now();
            const lastRender = Logger.renderTimes.get(this) || 0;

            // Only log if more than 1 second has passed since last render
            if (now - lastRender > 1000) {
                console.debug(this.formatMessage('debug', data));
                Logger.renderTimes.set(this, now);
            }
        }
    }

    private static renderTimes = new WeakMap<Logger, number>();
}

// Create the function first
function createLoggerImpl(options: LoggerOptions): Logger {
    return new Logger(options);
}

// Export with multiple patterns for maximum compatibility
export { createLoggerImpl as createLogger };
export default createLoggerImpl; 